/**
 * DesktopSignInModalHost — mounts the LARGE in-page sign-in surface over ANY
 * page (2026-08-18 UX overhaul, phase 6). Chat's browser lane dispatches a
 * `prest0n:desktop-signin` CustomEvent instead of handing the user a link to
 * go find — the same modal the Integrations page opens, popped over the
 * conversation, so the user never leaves the chat either.
 *
 * detail: { appName: string; sessionId?: string; customSlug?: string;
 *           signInUrl?: string; workspaceAccountId?: string;
 *           lang?: 'en' | 'es'; scope?: 'personal' | 'organization';
 *           origin?: { threadId: string; resumeText?: string } }
 *
 * ONE MOUNT (2026-09-03 owner directive: the sign-in surface "should both be
 * unified and the same"). The Integrations page used to mount the modal
 * itself, in parallel with this host, and the two drifted: this one carried a
 * handed-off session, a workspace target and the conversation's language;
 * that one carried a scope and an onConnected callback. The page now opens
 * the surface through this event like every other caller and hears about
 * completion through CONNECT_SIGNED_IN_EVENT, so there is one surface with
 * one set of capabilities wherever a user meets it.
 *
 * ON DEMAND (2026-09-30): the window itself is its own chunk, fetched through
 * its door (desktopSignInDoor.ts) at the first idle moment once someone is
 * signed in, or at once when a sign-in is asked for. This host stays on the
 * first download, so no request is ever missed: it keeps the request, with
 * the moment it was asked for, until the window arrives.
 *
 * A CARD EVERY TIME IT IS NEEDED (slice D, Miles's ruling R1, 2026-10-01).
 * This host is where the sign-in phase (browserSignInPhase.ts) starts and
 * ends: a request opens it, the window's close leaves it "not finished"
 * unless the person signed in, and it mounts the one painter that keeps
 * every sign-in card in the chat saying where the sign-in stands
 * (browserSignInCards.ts). A second request for the app whose window is
 * already open keeps that window (no second boot) and brings it forward. A
 * window that never arrives lets the request go, so the card stays usable.
 *
 * THE LIVE DESKTOP IN THIS TAB (2026-10-06, Miles's ruling). The window's
 * "open the live desktop" moves this tab to the /desktop page through the
 * page's own router (handed down here, so nothing reloads and this host, the
 * window and its sign-in all stay), and the window folds into a bar. Asking
 * for the same sign-in again brings a folded window back.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { useDesktopSignInModule } from '../services/composio/panel/desktopSignInDoor';
import { readConnectSignInOrigin, type ConnectSignInOrigin } from '../services/composio/connectSignedInEvent';
import { useConfirmedAuth } from '../hooks/useConfirmedAuth';
import {
  browserSignInKey,
  rememberBrowserSignInSlug,
  reportBrowserSignInPhase,
  reportBrowserSignInWindowClosed,
} from '../services/composio/browserSignInPhase';
import { installBrowserSignInCards, signInCardLive } from '../services/composio/browserSignInCards';

export const DESKTOP_SIGNIN_EVENT = 'prest0n:desktop-signin';
/** Late custom-connection slug for an ALREADY-OPEN surface (2026-08-26
 *  latency teardown). The connect flow used to await registration before
 *  opening the modal, spending a round trip behind a button with no pending
 *  state; it now opens first and sends the slug here when it lands. A second
 *  DESKTOP_SIGNIN_EVENT would re-open a surface the user had dismissed, so
 *  this one MERGES and never mounts. */
export const DESKTOP_SIGNIN_SLUG_EVENT = 'prest0n:desktop-signin-slug';

/** How long a request waits for the window to arrive before it lets go (slice D): the card then says the
 *  sign-in is not finished, and the next press asks again. */
export const SIGNIN_WINDOW_ARRIVAL_MS = 20_000;

/** Moves this tab to an address inside the app (the window's "open the live desktop"). */
type NavigateTo = (href: string, opts?: { replace?: boolean }) => void;

/** The page's own router, read where the host stands inside one (the app always does; a test may not). */
const WithNavigate: React.FC<{ children: (navigateTo: NavigateTo) => React.ReactElement }> = ({ children }) => {
  const navigate = useNavigate();
  return children((href, opts) => navigate(href, opts?.replace ? { replace: true } : undefined));
};

/** The open window, brought forward (the dialog PanelModal draws; it takes focus). */
function bringSignInWindowForward(): void {
  try {
    const el = document.querySelector('[data-testid="desktop-signin-modal"]') as HTMLElement | null;
    el?.focus();
  } catch {
    // Nothing to bring forward is not an error.
  }
}

interface SignInRequest {
  appName: string;
  sessionId?: string;
  customSlug?: string;
  signInUrl?: string;
  /** Explicit workstation target (2026-08-23): the org VM the sign-in must
   *  run on when the caller's back-pointer would resolve elsewhere (draft
   *  orgs mid-onboarding). */
  workspaceAccountId?: string;
  /** Language for the surface's code-authored failure lines (2026-09-02).
   *  The lane that opens the surface knows the conversation's language; the
   *  browser's own is the fallback inside the modal. */
  lang?: 'en' | 'es';
  /** Which plane the connection saves onto — the Integrations page's
   *  re-finish of an org connection is the one caller that is not
   *  'personal'. */
  scope?: 'personal' | 'organization';
  /** The conversation this sign-in interrupted, so it can be resumed. */
  origin?: ConnectSignInOrigin;
  /** When it was asked for: the window may arrive after the press, and its clock counts that. */
  openedAtMs: number;
}

export const DesktopSignInModalHost: React.FC = () => {
  const [request, setRequestState] = useState<SignInRequest | null>(null);
  // The request as the listeners must read it: they are installed once, so a closure over `request` would
  // hold the first render's copy.
  const requestRef = useRef<SignInRequest | null>(null);
  const setRequest = (next: SignInRequest | null | ((prev: SignInRequest | null) => SignInRequest | null)) => {
    const value = typeof next === 'function' ? next(requestRef.current) : next;
    requestRef.current = value;
    setRequestState(value);
  };
  const { signedIn } = useConfirmedAuth();
  const surface = useDesktopSignInModule(signedIn, request !== null);
  const inRouter = useInRouterContext();
  // Bumped when the same sign-in is asked for again: a window folded into its bar comes back.
  const [bringForward, setBringForward] = useState(0);
  // R1's live card (lever REACT_APP_SIGNIN_CARD_LIVE=off: the host as it was — no painter, a second request
  // replaces the first, and a request waits for its window however long it takes).
  const live = useRef(signInCardLive()).current;

  // The one painter for the chat's sign-in cards.
  useEffect(() => (live ? installBrowserSignInCards(document) : undefined), [live]);

  // A window that never arrives (its download failed for good) lets the request go: the card says the
  // sign-in is not finished, and the next press asks for the window again.
  useEffect(() => {
    if (!live || !request || surface) return undefined;
    const waiting = request;
    const timer = window.setTimeout(() => {
      if (requestRef.current !== waiting) return;
      reportBrowserSignInWindowClosed(waiting.appName);
      setRequest(null);
    }, SIGNIN_WINDOW_ARRIVAL_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, request, surface]);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as SignInRequest | undefined;
      if (!detail?.appName) return;
      const signInUrl = detail.signInUrl ? String(detail.signInUrl).trim() : '';
      const prev = requestRef.current;
      const asked = String(detail.appName).slice(0, 80);
      if (live && prev && browserSignInKey(prev.appName) === browserSignInKey(asked)) {
        // The same sign-in asked for again: the window stays (a second boot would throw away a sign-in half
        // typed), takes a slug it lacked, and comes forward.
        const slug = detail.customSlug ? String(detail.customSlug) : '';
        if (slug && !prev.customSlug) setRequest({ ...prev, customSlug: slug });
        setBringForward((n) => n + 1);
        bringSignInWindowForward();
        return;
      }
      if (prev) reportBrowserSignInWindowClosed(prev.appName);
      reportBrowserSignInPhase(asked, 'opening');
      setRequest({
        appName: asked,
        sessionId: detail.sessionId ? String(detail.sessionId) : undefined,
        customSlug: detail.customSlug ? String(detail.customSlug) : undefined,
        // https only — the URL seeds prest0n's own browser.
        signInUrl: /^https:\/\//i.test(signInUrl) ? signInUrl.slice(0, 500) : undefined,
        workspaceAccountId: detail.workspaceAccountId ? String(detail.workspaceAccountId).slice(0, 80) : undefined,
        // Only the two languages the code-authored lines exist in; anything
        // else leaves the modal on the browser's own language.
        lang: detail.lang === 'es' || detail.lang === 'en' ? detail.lang : undefined,
        scope: detail.scope === 'organization' ? 'organization' : 'personal',
        // Malformed → no origin. Resuming the WRONG thread is worse than not
        // resuming at all, so this never guesses.
        origin: readConnectSignInOrigin((detail as { origin?: unknown }).origin),
        openedAtMs: Date.now(),
      });
    };
    // Late slug: merge into the OPEN surface only. Same appName guard so a
    // stale registration from a dismissed-then-reopened app can't cross-label
    // the current one. appName/signInUrl/workspaceAccountId are unchanged, so
    // the modal's boot effect (keyed on those) does not restart — the session
    // already in flight is never interrupted by this.
    const onSlug = (e: Event) => {
      const detail = (e as CustomEvent).detail as { appName?: string; customSlug?: string } | undefined;
      const slug = detail?.customSlug ? String(detail.customSlug) : '';
      if (!slug || !detail?.appName) return;
      const forApp = String(detail.appName).slice(0, 80);
      // The slug the person's own press registered: every card for the app learns it, and the next press
      // reopens on it (browserSignInPhase.ts keeps only such a slug).
      rememberBrowserSignInSlug(forApp, slug);
      setRequest((prev) => (prev && prev.appName === forApp && !prev.customSlug
        ? { ...prev, customSlug: slug }
        : prev));
    };
    document.addEventListener(DESKTOP_SIGNIN_EVENT, onEvent as EventListener);
    document.addEventListener(DESKTOP_SIGNIN_SLUG_EVENT, onSlug as EventListener);
    return () => {
      document.removeEventListener(DESKTOP_SIGNIN_EVENT, onEvent as EventListener);
      document.removeEventListener(DESKTOP_SIGNIN_SLUG_EVENT, onSlug as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Until the window has arrived the request waits here; nothing is drawn in the meantime.
  if (!request || !surface) return null;
  const DesktopSignInModal = surface.default;
  const windowFor = (navigateTo?: NavigateTo) => (
    <DesktopSignInModal
      appName={request.appName}
      sessionId={request.sessionId}
      customSlug={request.customSlug}
      signInUrl={request.signInUrl}
      workspaceAccountId={request.workspaceAccountId}
      lang={request.lang}
      scope={request.scope}
      origin={request.origin}
      openedAtMs={request.openedAtMs}
      {...(navigateTo ? { navigateTo } : {})}
      bringForward={bringForward}
      onClose={() => {
        // Closed before the person signed in: the card says so, and stays usable.
        reportBrowserSignInWindowClosed(request.appName);
        setRequest(null);
      }}
    />
  );
  return inRouter ? <WithNavigate>{windowFor}</WithNavigate> : windowFor();
};

export default DesktopSignInModalHost;
